import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const TermsOfService = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const effectiveDate = "February 10, 2026";
  const companyName = "Estimate Assistant";
  const companyEmail = "support@estimateassistant.com";
  const subscriptionPrice = "$29";

  return (
    <div className="terms-page">
      <div className="terms-container">
        {/* Header */}
        <header className="terms-header">
          <Link to="/" className="back-link">
            ← Back to Home
          </Link>
          <h1>Terms of Service</h1>
          <p className="effective-date">Effective Date: {effectiveDate}</p>
        </header>

        {/* Quick Summary */}
        <div className="terms-summary">
          <h2>📋 Quick Summary</h2>
          <p>
            This is a legal agreement between you and {companyName}. Here's the gist:
          </p>
          <ul>
            <li><strong>What we provide:</strong> A SaaS tool to create construction estimates</li>
            <li><strong>Estimates are guides:</strong> Prices are market averages, not guarantees</li>
            <li><strong>Your responsibility:</strong> Verify all pricing before using with clients</li>
            <li><strong>Subscription:</strong> {subscriptionPrice}/month, cancel anytime</li>
            <li><strong>Your data:</strong> You own your estimates; we just host them</li>
          </ul>
          <p className="summary-note">
            Please read the full terms below. By using {companyName}, you agree to these terms.
          </p>
        </div>

        {/* Table of Contents */}
        <nav className="terms-toc">
          <h3>Contents</h3>
          <ol>
            <li><a href="#service-description">Service Description</a></li>
            <li><a href="#account-registration">Account Registration</a></li>
            <li><a href="#subscription-payment">Subscription & Payment</a></li>
            <li><a href="#estimate-disclaimer">Estimate Disclaimer</a></li>
            <li><a href="#user-responsibilities">User Responsibilities</a></li>
            <li><a href="#acceptable-use">Acceptable Use</a></li>
            <li><a href="#intellectual-property">Intellectual Property</a></li>
            <li><a href="#data-privacy">Data & Privacy</a></li>
            <li><a href="#limitation-liability">Limitation of Liability</a></li>
            <li><a href="#indemnification">Indemnification</a></li>
            <li><a href="#termination">Termination</a></li>
            <li><a href="#modifications">Modifications to Terms</a></li>
            <li><a href="#governing-law">Governing Law</a></li>
            <li><a href="#contact">Contact Information</a></li>
          </ol>
        </nav>

        {/* Terms Content */}
        <div className="terms-content">
          
          {/* 1. Service Description */}
          <section id="service-description">
            <h2>1. Service Description</h2>
            <p>
              {companyName} ("we," "us," "our") provides a web-based software application 
              ("Service") that enables contractors, builders, and construction professionals 
              ("Users," "you") to create, manage, and generate professional project estimates.
            </p>
            <p>Our Service includes:</p>
            <ul>
              <li>AI-powered material list generation based on project descriptions</li>
              <li>Access to a pricing database with market-average material costs</li>
              <li>Professional PDF estimate generation</li>
              <li>Equipment rental pricing reference</li>
              <li>Project and estimate management tools</li>
              <li>Cloud storage for your estimates and company information</li>
            </ul>
            <p>
              The Service is provided on a subscription basis and is accessible via web 
              browsers on desktop and mobile devices.
            </p>
          </section>

          {/* 2. Account Registration */}
          <section id="account-registration">
            <h2>2. Account Registration</h2>
            <p>To use {companyName}, you must:</p>
            <ul>
              <li>Create an account with accurate and complete information</li>
              <li>Be at least 18 years of age or the legal age of majority in your jurisdiction</li>
              <li>Have the legal authority to enter into this agreement</li>
              <li>Keep your login credentials secure and confidential</li>
            </ul>
            <p>
              You are responsible for all activity that occurs under your account. If you 
              suspect unauthorized access, notify us immediately at {companyEmail}.
            </p>
            <p>
              We reserve the right to suspend or terminate accounts that violate these 
              terms or engage in fraudulent activity.
            </p>
          </section>

          {/* 3. Subscription & Payment */}
          <section id="subscription-payment">
            <h2>3. Subscription & Payment</h2>
            
            <h3>3.1 Subscription Plans</h3>
            <p>
              {companyName} is offered as a monthly subscription service at {subscriptionPrice} per 
              month. We may offer additional plans, promotional pricing, or annual billing 
              options at our discretion.
            </p>

            <h3>3.2 Free Trial</h3>
            <p>
              New users may be eligible for a 7-day free trial. During the trial period, 
              you have full access to all features. If you do not cancel before the trial 
              ends, your subscription will automatically begin and you will be charged.
            </p>

            <h3>3.3 Billing</h3>
            <ul>
              <li>Subscriptions are billed monthly in advance</li>
              <li>Payment is processed through Stripe, our third-party payment processor</li>
              <li>You authorize us to charge your payment method on file each billing cycle</li>
              <li>All fees are in U.S. dollars unless otherwise specified</li>
            </ul>

            <h3>3.4 Cancellation</h3>
            <p>
              You may cancel your subscription at any time through your account settings. 
              Upon cancellation:
            </p>
            <ul>
              <li>You will retain access until the end of your current billing period</li>
              <li>No refunds are provided for partial months</li>
              <li>Your data will be retained for 30 days, after which it may be deleted</li>
            </ul>

            <h3>3.5 Price Changes</h3>
            <p>
              We reserve the right to modify subscription pricing. Any price changes will 
              be communicated at least 30 days in advance and will apply to your next 
              billing cycle.
            </p>
          </section>

          {/* 4. Estimate Disclaimer */}
          <section id="estimate-disclaimer" className="highlight-section">
            <h2>4. Estimate Disclaimer</h2>
            <div className="important-notice">
              <span className="notice-icon">⚠️</span>
              <div>
                <strong>Important:</strong> Please read this section carefully as it 
                describes the limitations of our pricing data.
              </div>
            </div>
            
            <h3>4.1 Estimates Are Reference Only</h3>
            <p>
              All pricing information, material costs, labor rates, and estimates generated 
              through {companyName} are provided <strong>for reference purposes only</strong>. 
              They represent market averages and general guidance, not actual quotes or 
              guaranteed prices.
            </p>

            <h3>4.2 Price Variability</h3>
            <p>Actual prices may vary significantly based on:</p>
            <ul>
              <li>Geographic location and regional market conditions</li>
              <li>Current supply chain conditions and material availability</li>
              <li>Seasonal fluctuations in demand</li>
              <li>Specific product brands, quality levels, and specifications</li>
              <li>Supplier relationships and negotiated discounts</li>
              <li>Project complexity and site conditions</li>
              <li>Labor market conditions in your area</li>
            </ul>

            <h3>4.3 User Verification Required</h3>
            <p>
              <strong>You are solely responsible for verifying all pricing</strong> with 
              actual suppliers, subcontractors, and material vendors before presenting 
              estimates to clients or entering into contracts. We strongly recommend:
            </p>
            <ul>
              <li>Obtaining actual quotes from your suppliers before finalizing estimates</li>
              <li>Adding appropriate contingency margins to account for price fluctuations</li>
              <li>Regularly updating your pricing based on current market conditions</li>
              <li>Clearly communicating to clients that estimates are subject to change</li>
            </ul>

            <h3>4.4 No Guarantee of Accuracy</h3>
            <p>
              While we strive to maintain accurate and up-to-date pricing information, 
              we make <strong>no warranties or guarantees</strong> regarding the accuracy, 
              completeness, or timeliness of any pricing data in our system. Construction 
              material prices can change daily, and our database may not reflect the most 
              current pricing.
            </p>

            <h3>4.5 Your Responsibility</h3>
            <p>
              Any estimates you provide to clients based on data from {companyName} are 
              your sole responsibility. You bear all risk associated with pricing decisions, 
              and we shall not be liable for any losses, damages, or disputes arising from 
              inaccurate pricing or estimates.
            </p>
          </section>

          {/* 5. User Responsibilities */}
          <section id="user-responsibilities">
            <h2>5. User Responsibilities</h2>
            <p>As a user of {companyName}, you agree to:</p>
            <ul>
              <li>Use the Service only for lawful business purposes</li>
              <li>Provide accurate information when creating estimates</li>
              <li>Verify all pricing and materials before presenting to clients</li>
              <li>Maintain appropriate professional licenses required in your jurisdiction</li>
              <li>Comply with all applicable laws, regulations, and industry standards</li>
              <li>Not misrepresent estimates as guaranteed or binding quotes unless you have 
                  verified all pricing independently</li>
              <li>Keep your account credentials secure</li>
              <li>Promptly update your account information if it changes</li>
            </ul>
          </section>

          {/* 6. Acceptable Use */}
          <section id="acceptable-use">
            <h2>6. Acceptable Use</h2>
            <p>You agree NOT to:</p>
            <ul>
              <li>Use the Service for any illegal or unauthorized purpose</li>
              <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Reverse engineer, decompile, or attempt to extract source code</li>
              <li>Use automated systems (bots, scrapers) to access the Service</li>
              <li>Resell, redistribute, or sublicense access to the Service</li>
              <li>Upload malicious code, viruses, or harmful content</li>
              <li>Harass, abuse, or harm other users or our staff</li>
              <li>Use the Service to create fraudulent or misleading estimates</li>
              <li>Violate any applicable laws or regulations</li>
            </ul>
            <p>
              Violation of these terms may result in immediate termination of your account 
              without refund.
            </p>
          </section>

          {/* 7. Intellectual Property */}
          <section id="intellectual-property">
            <h2>7. Intellectual Property</h2>
            
            <h3>7.1 Our Intellectual Property</h3>
            <p>
              {companyName}, including its software, design, logos, trademarks, pricing 
              databases, AI models, and all related intellectual property, is owned by us 
              and protected by copyright, trademark, and other intellectual property laws.
            </p>
            <p>
              Your subscription grants you a limited, non-exclusive, non-transferable 
              license to use the Service for your internal business purposes only.
            </p>

            <h3>7.2 Your Content</h3>
            <p>
              You retain ownership of all content you create using our Service, including:
            </p>
            <ul>
              <li>Estimates and project data you create</li>
              <li>Client information you enter</li>
              <li>Company branding and logos you upload</li>
              <li>Custom materials and pricing you add</li>
            </ul>
            <p>
              By using our Service, you grant us a limited license to host, store, and 
              display your content solely for the purpose of providing the Service to you.
            </p>

            <h3>7.3 Feedback</h3>
            <p>
              If you provide suggestions, feedback, or ideas about the Service, you grant 
              us the right to use this feedback without any obligation to you.
            </p>
          </section>

          {/* 8. Data & Privacy */}
          <section id="data-privacy">
            <h2>8. Data & Privacy</h2>
            <p>
              Your privacy is important to us. Our collection and use of personal 
              information is governed by our <Link to="/privacy">Privacy Policy</Link>, 
              which is incorporated into these Terms by reference.
            </p>
            <p>Key points:</p>
            <ul>
              <li>We collect only the data necessary to provide the Service</li>
              <li>Your estimate and client data is stored securely</li>
              <li>We do not sell your personal information to third parties</li>
              <li>You can export or delete your data upon request</li>
              <li>We use industry-standard security measures to protect your data</li>
            </ul>
          </section>

          {/* 9. Limitation of Liability */}
          <section id="limitation-liability" className="highlight-section">
            <h2>9. Limitation of Liability</h2>
            
            <h3>9.1 Disclaimer of Warranties</h3>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF 
              ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED 
              WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND 
              NON-INFRINGEMENT.
            </p>
            <p>
              We do not warrant that:
            </p>
            <ul>
              <li>The Service will be uninterrupted, secure, or error-free</li>
              <li>Pricing data will be accurate, complete, or current</li>
              <li>Results obtained from the Service will meet your requirements</li>
              <li>Any errors in the Service will be corrected</li>
            </ul>

            <h3>9.2 Limitation of Liability</h3>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, {companyName.toUpperCase()}, ITS 
              OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR:
            </p>
            <ul>
              <li>Any indirect, incidental, special, consequential, or punitive damages</li>
              <li>Loss of profits, revenue, data, or business opportunities</li>
              <li>Damages arising from inaccurate pricing or estimates</li>
              <li>Damages arising from your use of or inability to use the Service</li>
              <li>Damages arising from unauthorized access to your account</li>
              <li>Any disputes between you and your clients</li>
            </ul>
            <p>
              IN NO EVENT SHALL OUR TOTAL LIABILITY EXCEED THE AMOUNT YOU PAID US IN THE 
              TWELVE (12) MONTHS PRECEDING THE CLAIM.
            </p>

            <h3>9.3 Essential Purpose</h3>
            <p>
              You acknowledge that we have set our prices and entered into this agreement 
              in reliance upon the limitations of liability set forth herein, which 
              allocate risk between us and form an essential basis of the bargain between 
              the parties.
            </p>
          </section>

          {/* 10. Indemnification */}
          <section id="indemnification">
            <h2>10. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless {companyName} and its 
              officers, directors, employees, agents, and affiliates from and against any 
              claims, damages, losses, liabilities, costs, and expenses (including 
              reasonable attorneys' fees) arising from:
            </p>
            <ul>
              <li>Your use of the Service</li>
              <li>Estimates you create or provide to third parties</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any applicable law or regulation</li>
              <li>Disputes with your clients or customers</li>
              <li>Your negligence or misconduct</li>
            </ul>
          </section>

          {/* 11. Termination */}
          <section id="termination">
            <h2>11. Termination</h2>
            
            <h3>11.1 Termination by You</h3>
            <p>
              You may terminate your account at any time by canceling your subscription 
              through your account settings or by contacting us at {companyEmail}.
            </p>

            <h3>11.2 Termination by Us</h3>
            <p>We may suspend or terminate your account immediately if:</p>
            <ul>
              <li>You violate these Terms of Service</li>
              <li>You fail to pay subscription fees when due</li>
              <li>We are required to do so by law</li>
              <li>Your use poses a security risk to the Service or others</li>
              <li>You engage in fraudulent or illegal activity</li>
            </ul>

            <h3>11.3 Effect of Termination</h3>
            <p>Upon termination:</p>
            <ul>
              <li>Your right to access the Service will immediately cease</li>
              <li>We may delete your data after 30 days (you may request export before termination)</li>
              <li>Provisions that by their nature should survive termination will survive 
                  (including limitations of liability, indemnification, and intellectual property)</li>
              <li>No refunds will be provided for partial billing periods</li>
            </ul>
          </section>

          {/* 12. Modifications to Terms */}
          <section id="modifications">
            <h2>12. Modifications to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. When we make changes:
            </p>
            <ul>
              <li>We will post the updated Terms on our website</li>
              <li>We will update the "Effective Date" at the top</li>
              <li>For material changes, we will notify you via email or in-app notification 
                  at least 30 days before the changes take effect</li>
            </ul>
            <p>
              Your continued use of the Service after changes become effective constitutes 
              your acceptance of the revised Terms. If you do not agree to the modified 
              Terms, you must stop using the Service and cancel your subscription.
            </p>
          </section>

          {/* 13. Governing Law */}
          <section id="governing-law">
            <h2>13. Governing Law & Dispute Resolution</h2>
            
            <h3>13.1 Governing Law</h3>
            <p>
              These Terms shall be governed by and construed in accordance with the laws 
              of the State of New York, without regard to its conflict of law provisions.
            </p>

            <h3>13.2 Dispute Resolution</h3>
            <p>
              Any dispute arising from these Terms or your use of the Service shall first 
              be attempted to be resolved through good-faith negotiation. If negotiation 
              fails, disputes shall be resolved through binding arbitration in accordance 
              with the rules of the American Arbitration Association.
            </p>

            <h3>13.3 Class Action Waiver</h3>
            <p>
              You agree that any dispute resolution proceedings will be conducted only on 
              an individual basis and not in a class, consolidated, or representative action.
            </p>

            <h3>13.4 Time Limitation</h3>
            <p>
              Any claim or cause of action arising from these Terms or your use of the 
              Service must be filed within one (1) year after such claim arose, or be 
              forever barred.
            </p>
          </section>

          {/* 14. General Provisions */}
          <section id="general">
            <h2>14. General Provisions</h2>
            
            <h3>14.1 Entire Agreement</h3>
            <p>
              These Terms, together with our Privacy Policy, constitute the entire 
              agreement between you and {companyName} regarding the Service.
            </p>

            <h3>14.2 Severability</h3>
            <p>
              If any provision of these Terms is found to be unenforceable, the remaining 
              provisions will continue in full force and effect.
            </p>

            <h3>14.3 Waiver</h3>
            <p>
              Our failure to enforce any right or provision of these Terms will not be 
              considered a waiver of that right or provision.
            </p>

            <h3>14.4 Assignment</h3>
            <p>
              You may not assign or transfer these Terms without our prior written consent. 
              We may assign our rights and obligations without restriction.
            </p>

            <h3>14.5 Force Majeure</h3>
            <p>
              We shall not be liable for any failure or delay in performing our obligations 
              due to circumstances beyond our reasonable control, including natural disasters, 
              war, terrorism, labor disputes, or government actions.
            </p>
          </section>

          {/* 15. Contact Information */}
          <section id="contact">
            <h2>15. Contact Information</h2>
            <p>
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <div className="contact-info">
              <p><strong>{companyName}</strong></p>
              <p>Email: <a href={`mailto:${companyEmail}`}>{companyEmail}</a></p>
              <p>Website: <a href="https://estimateassistant.com">estimateassistant.com</a></p>
            </div>
          </section>

        </div>

        {/* Footer */}
        <footer className="terms-footer">
          <p>
            By using {companyName}, you acknowledge that you have read, understood, and 
            agree to be bound by these Terms of Service.
          </p>
          <div className="footer-links">
            <Link to="/privacy">Privacy Policy</Link>
            <span>•</span>
            <Link to="/">Return to Home</Link>
          </div>
        </footer>
      </div>

      <style>{`
        .terms-page {
          min-height: 100vh;
          background: #f9fafb;
          padding: 2rem 1rem;
        }

        .terms-container {
          max-width: 800px;
          margin: 0 auto;
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
          overflow: hidden;
        }

        /* Header */
        .terms-header {
          background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
          color: white;
          padding: 2rem;
          text-align: center;
        }

        .back-link {
          display: inline-block;
          color: rgba(255, 255, 255, 0.8);
          text-decoration: none;
          font-size: 0.9rem;
          margin-bottom: 1rem;
          transition: color 0.2s;
        }

        .back-link:hover {
          color: white;
        }

        .terms-header h1 {
          font-size: 2rem;
          margin: 0 0 0.5rem 0;
          font-weight: 700;
        }

        .effective-date {
          margin: 0;
          opacity: 0.9;
          font-size: 0.95rem;
        }

        /* Quick Summary */
        .terms-summary {
          background: #eff6ff;
          border-left: 4px solid #3b82f6;
          margin: 2rem;
          padding: 1.5rem;
          border-radius: 8px;
        }

        .terms-summary h2 {
          font-size: 1.1rem;
          color: #1e40af;
          margin: 0 0 0.75rem 0;
        }

        .terms-summary p {
          margin: 0 0 0.75rem 0;
          color: #374151;
        }

        .terms-summary ul {
          margin: 0 0 0.75rem 0;
          padding-left: 1.25rem;
          color: #374151;
        }

        .terms-summary li {
          margin-bottom: 0.4rem;
        }

        .summary-note {
          font-size: 0.9rem;
          color: #6b7280;
          font-style: italic;
          margin-bottom: 0 !important;
        }

        /* Table of Contents */
        .terms-toc {
          margin: 0 2rem 2rem;
          padding: 1.5rem;
          background: #f9fafb;
          border-radius: 8px;
        }

        .terms-toc h3 {
          font-size: 1rem;
          color: #374151;
          margin: 0 0 1rem 0;
        }

        .terms-toc ol {
          margin: 0;
          padding-left: 1.25rem;
          columns: 2;
          column-gap: 2rem;
        }

        @media (max-width: 600px) {
          .terms-toc ol {
            columns: 1;
          }
        }

        .terms-toc li {
          margin-bottom: 0.5rem;
          break-inside: avoid;
        }

        .terms-toc a {
          color: #1e40af;
          text-decoration: none;
          font-size: 0.9rem;
        }

        .terms-toc a:hover {
          text-decoration: underline;
        }

        /* Content */
        .terms-content {
          padding: 0 2rem;
        }

        .terms-content section {
          padding: 1.5rem 0;
          border-bottom: 1px solid #e5e7eb;
        }

        .terms-content section:last-child {
          border-bottom: none;
        }

        .terms-content h2 {
          font-size: 1.35rem;
          color: #1f2937;
          margin: 0 0 1rem 0;
          padding-top: 0.5rem;
        }

        .terms-content h3 {
          font-size: 1.05rem;
          color: #374151;
          margin: 1.25rem 0 0.75rem 0;
        }

        .terms-content p {
          color: #4b5563;
          line-height: 1.7;
          margin: 0 0 1rem 0;
        }

        .terms-content ul {
          margin: 0 0 1rem 0;
          padding-left: 1.5rem;
          color: #4b5563;
        }

        .terms-content li {
          margin-bottom: 0.5rem;
          line-height: 1.6;
        }

        .terms-content a {
          color: #1e40af;
          text-decoration: none;
        }

        .terms-content a:hover {
          text-decoration: underline;
        }

        /* Highlight sections */
        .highlight-section {
          background: #fffbeb;
          margin: 0 -2rem;
          padding: 1.5rem 2rem !important;
          border-left: 4px solid #f59e0b;
        }

        .important-notice {
          display: flex;
          gap: 0.75rem;
          align-items: flex-start;
          background: white;
          padding: 1rem;
          border-radius: 8px;
          border: 1px solid #fcd34d;
          margin-bottom: 1rem;
        }

        .notice-icon {
          font-size: 1.25rem;
          flex-shrink: 0;
        }

        .important-notice strong {
          color: #92400e;
        }

        /* Contact info */
        .contact-info {
          background: #f9fafb;
          padding: 1.25rem;
          border-radius: 8px;
          margin-top: 1rem;
        }

        .contact-info p {
          margin: 0.25rem 0;
        }

        /* Footer */
        .terms-footer {
          background: #f9fafb;
          padding: 1.5rem 2rem;
          margin-top: 2rem;
          text-align: center;
        }

        .terms-footer p {
          color: #6b7280;
          font-size: 0.9rem;
          margin: 0 0 1rem 0;
        }

        .footer-links {
          display: flex;
          justify-content: center;
          gap: 1rem;
          align-items: center;
        }

        .footer-links a {
          color: #1e40af;
          text-decoration: none;
          font-size: 0.9rem;
        }

        .footer-links a:hover {
          text-decoration: underline;
        }

        .footer-links span {
          color: #9ca3af;
        }

        /* Responsive */
        @media (max-width: 640px) {
          .terms-page {
            padding: 1rem 0.5rem;
          }

          .terms-header {
            padding: 1.5rem;
          }

          .terms-header h1 {
            font-size: 1.5rem;
          }

          .terms-summary,
          .terms-toc {
            margin: 1.5rem 1rem;
          }

          .terms-content {
            padding: 0 1.25rem;
          }

          .highlight-section {
            margin: 0 -1.25rem;
            padding: 1.25rem !important;
          }

          .terms-footer {
            padding: 1.25rem;
          }
        }

        /* Print styles */
        @media print {
          .terms-page {
            background: white;
            padding: 0;
          }

          .terms-container {
            box-shadow: none;
          }

          .back-link {
            display: none;
          }

          .terms-header {
            background: #1e40af;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      `}</style>
    </div>
  );
};

export default TermsOfService;
